"use client";

import type { CartBatchUpdateInputDto, CartOutputDto, ShippingInfoInputDto } from "@sticker-giant/sg-frontend-common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CustomerFacingErrorMessages } from "@/constants/CustomerFacingErrorMessages";
import { CART_ACTIVE_FILTER } from "@/queries/useCart";
import { useGlobalStore } from "@/stores/useGlobalStore";
import { useServicesStore } from "@/stores/useServicesStore";

export const deliveryOptionMutationKey = ["deliveryOption"] as const;

type DeliveryOptionMutationInput = {
  shippingInfo: ShippingInfoInputDto;
  isExpedited?: boolean;
};

export function useDeliveryOptionMutation() {
  const cartClient = useServicesStore(state => state.cartClient);
  const cartId = useGlobalStore(state => state.cartId);
  const queryClient = useQueryClient();

  return useMutation<CartOutputDto, Error, DeliveryOptionMutationInput>({
    mutationKey: deliveryOptionMutationKey,
    mutationFn: async ({ shippingInfo, isExpedited }) => {
      if (!cartClient) throw new Error("Cart client is not available.");
      if (!cartId) throw new Error("Cart id is not available.");

      const input: CartBatchUpdateInputDto = {
        shippingInfo,
        ...(isExpedited !== undefined && { expeditedInfo: { isExpedited } }),
      };

      return await cartClient.batchUpdateItems(cartId, input, CustomerFacingErrorMessages.unableToUpdateDeliveryOption);
    },
    onSuccess: cart => {
      queryClient.setQueriesData<CartOutputDto | null>(CART_ACTIVE_FILTER, cart);
    },
  });
}
